// Vendor Purchase History — screen.
// Mirrors src/Components/procurement.tsx's list+Sheet wiring (search/sort/
// paging state, +Add button, success toast + re-fetch). Add, Edit and a
// confirmed soft Delete (isDeleted = 1 through the same create/update call).
import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import moment from 'moment'
import { DateRange } from 'react-day-picker'
import { useDispatch, useSelector } from 'react-redux';
import ApparelMenu from "../SubComponents/apparelMenu";
import { getVendorPurchaseListAction, createOrUpdateVendorPurchaseAction, vendorDropDownListAction } from '../../Store/actions/apparelAction';
import AppConstant from '../../Globals/AppConstants';
import { Button } from 'ui/button';
import { ConfirmDialog } from '../../shared/confirm-dialog';
import VendorPurchaseTable from './VendorPurchaseTable';
import VendorPurchaseFormSheet from './VendorPurchaseFormSheet';
import VendorBillAttachmentViewer from './VendorBillAttachmentViewer';
import VendorBillAttachmentSheet from './VendorBillAttachmentSheet';

const vendorPurchaseFormTemp = {
    vendorPurchaseId: "",
    invoiceId: "",
    vendorId: null,
    purchaseDate: "",
    gpayAmount: "",
    cashAmount: "",
    shippingCharges: "",
}

const VendorPurchaseHistory = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    // Arriving from Audit Log: fetch this one record directly by its
    // uniqueKey (no existing filter here can isolate a purchase by code),
    // then open its edit Sheet the same way a row click does.
    const [pendingAuditRecordId, setPendingAuditRecordId] = useState<string | undefined>(location.state?.auditRecordId);
    const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
    const {
        getVendorPurchaseListData, getVendorPurchaseListOnLoad,
        createOrUpdateVendorPurchaseOnLoad, createOrUpdateVendorPurchaseData,
        genVendorDropDownListData,
    } = apparelReducerState;

    const vendorPurchaseList = getVendorPurchaseListData?.getVendorPurchaseList?.vendorPurchaseDetails;
    const vendorPurchasePage = getVendorPurchaseListData?.getVendorPurchaseList?.page;
    const getVendorDropDownList = genVendorDropDownListData?.getVendorDropDownList;

    const [open, setOpen] = useState(false);
    const [pendingVendorBillIsEdit, setPendingVendorBillIsEdit] = useState(false);
    const [billAttachmentOpen, setBillAttachmentOpen] = useState(false);
    const [billAttachmentEntityId, setBillAttachmentEntityId] = useState<any>(null);
    const [billAttachmentSheetOpen, setBillAttachmentSheetOpen] = useState(false);
    const [billAttachmentSheetEntityId, setBillAttachmentSheetEntityId] = useState<any>(null);
    const [createOrUpdateVendorPurchase, setCreateOrUpdateVendorPurchase] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<any>(null);
    const [deletePending, setDeletePending] = useState(false);
    const [vendorPurchaseFormData, setVendorPurchaseFormData] = useState<any>(vendorPurchaseFormTemp);
    const [searchTemp, setSearchTemp] = useState({
        vendorId: "",
        fromDate: "",
        toDate: "",
        offset: 0,
        limit: 10,
        sort: 0,
        page: 1
    });
    const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

    const vendorComboOptions = (getVendorDropDownList || []).map((v: any) => ({
        value: String(v?.vendorId),
        label: v?.name + (v?.contactName ? " - " + v?.contactName : ""),
    }));

    const onVendorFilterChange = (vendorId: string) => {
        setSearchTemp({ ...searchTemp, vendorId, offset: 0 });
    }

    const onDateRangeChange = (range: DateRange | undefined) => {
        setDateRange(range);
        setSearchTemp({
            ...searchTemp,
            fromDate: range?.from ? moment(range.from).format('YYYY-MM-DD') : "",
            toDate: range?.to ? moment(range.to).format('YYYY-MM-DD') : "",
            offset: 0
        });
    }

    const onPageChange = (nextPage: number) => {
        let offset = ((nextPage * 10) - 10 === 0) ? 0 : (nextPage * 10) - 10;
        setSearchTemp({ ...searchTemp, page: nextPage, offset: offset });
    }

    const getFilterSortDataList = (e: any) => {
        setSearchTemp({ ...searchTemp, sort: e });
    }

    const getVendorPurchaseListApi = () => {
        let payload = {
            vendorId: searchTemp?.vendorId,
            fromDate: searchTemp?.fromDate,
            toDate: searchTemp?.toDate,
            sortBy: searchTemp?.sort,
            vendorPurchaseId: pendingAuditRecordId,
            paging: {
                limit: 10,
                offset: searchTemp?.offset
            }
        }
        dispatch(getVendorPurchaseListAction(payload));
    }

    useEffect(() => {
        getVendorPurchaseListApi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTemp])

    useEffect(() => {
        dispatch(vendorDropDownListAction());
        getVendorPurchaseListApi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetForm = () => {
        setVendorPurchaseFormData(vendorPurchaseFormTemp);
    }

    const createOrUpdateVendorPurchaseApi = () => {
        setPendingVendorBillIsEdit(!!vendorPurchaseFormData?.vendorPurchaseId);
        let payload = {
            vendorPurchaseId: vendorPurchaseFormData?.vendorPurchaseId,
            vendorId: vendorPurchaseFormData?.vendorId,
            purchaseDate: vendorPurchaseFormData?.purchaseDate,
            gpayAmount: Number(vendorPurchaseFormData?.gpayAmount) || 0,
            cashAmount: Number(vendorPurchaseFormData?.cashAmount) || 0,
            shippingCharges: Number(vendorPurchaseFormData?.shippingCharges) || 0,
        }
        dispatch(createOrUpdateVendorPurchaseAction(payload));
        setOpen(false);
        setCreateOrUpdateVendorPurchase(true);
        resetForm();
    }

    useEffect(() => {
        if (createOrUpdateVendorPurchase && !createOrUpdateVendorPurchaseOnLoad) {
            toast.success(pendingVendorBillIsEdit ? "Vendor Bill updated successfully" : "Vendor Bill added successfully");
            getVendorPurchaseListApi();
            setCreateOrUpdateVendorPurchase(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createOrUpdateVendorPurchaseOnLoad, createOrUpdateVendorPurchase]);

    // Soft delete — resubmits the row's own values with isDeleted = 1.
    const confirmDelete = () => {
        setDeletePending(true);
        dispatch(createOrUpdateVendorPurchaseAction({
            vendorPurchaseId: deleteTarget?.vendorPurchaseId,
            vendorId: deleteTarget?.vendorId,
            purchaseDate: deleteTarget?.purchaseDate ? String(deleteTarget.purchaseDate).slice(0, 10) : "",
            gpayAmount: Number(deleteTarget?.gpayAmount) || 0,
            cashAmount: Number(deleteTarget?.cashAmount) || 0,
            shippingCharges: Number(deleteTarget?.shippingCharges) || 0,
            isDeleted: 1,
        }));
    }

    // The mutation answers with a message string; only "Deleted Successfully"
    // means the bill is gone.
    useEffect(() => {
        if (!deletePending || createOrUpdateVendorPurchaseOnLoad) return;
        const result = createOrUpdateVendorPurchaseData?.createOrUpdateVendorPurchase;
        setDeletePending(false);
        setDeleteTarget(null);
        if (result !== "Deleted Successfully") {
            toast.error(typeof result === "string" ? result : "Something went wrong");
            return;
        }
        toast.success("Vendor Bill deleted successfully");
        // Deleting the only row on a later page steps back a page.
        if ((vendorPurchaseList || []).length === 1 && searchTemp.page > 1) {
            onPageChange(searchTemp.page - 1);
            return;
        }
        getVendorPurchaseListApi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createOrUpdateVendorPurchaseOnLoad, deletePending]);

    const formClose = () => {
        setOpen(false);
        resetForm();
    }

    const onBillAttachment = (data: any) => {
        setBillAttachmentEntityId(data?.vendorPurchaseId);
        setBillAttachmentOpen(true);
    }

    const onBillAttachmentSheet = (data: any) => {
        setBillAttachmentSheetEntityId(data?.vendorPurchaseId);
        setBillAttachmentSheetOpen(true);
    }

    const onEdit = (data: any) => {
        setVendorPurchaseFormData({
            vendorPurchaseId: data?.vendorPurchaseId,
            invoiceId: data?.invoiceId,
            vendorId: data?.vendorId,
            purchaseDate: data?.purchaseDate ? String(data.purchaseDate).slice(0, 10) : "",
            gpayAmount: data?.gpayAmount != null ? String(Math.round(Number(data.gpayAmount))) : "",
            cashAmount: data?.cashAmount != null ? String(Math.round(Number(data.cashAmount))) : "",
            shippingCharges: data?.shippingCharges != null ? String(Math.round(Number(data.shippingCharges))) : "",
        })
        setOpen(true);
    }

    useEffect(() => {
        if (!pendingAuditRecordId || !vendorPurchaseList?.length) return;
        const match = vendorPurchaseList.find((row: any) => row?.vendorPurchaseId === pendingAuditRecordId);
        if (match) {
            onEdit(match);
            setPendingAuditRecordId(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingAuditRecordId, vendorPurchaseList])

    return (
        <div className="mz h-full overflow-y-auto px-6 py-4">
            <ApparelMenu selectedKey="vendor-purchase" />
            <VendorPurchaseTable
                data={vendorPurchaseList}
                loading={getVendorPurchaseListOnLoad}
                onEdit={onEdit}
                onDelete={setDeleteTarget}
                onBillAttachment={onBillAttachment}
                onBillAttachmentSheet={onBillAttachmentSheet}
                vendorOptions={vendorComboOptions}
                vendorFilter={searchTemp?.vendorId}
                onVendorFilterChange={onVendorFilterChange}
                dateRange={dateRange}
                onDateRangeChange={onDateRangeChange}
                sortBy={searchTemp?.sort}
                onSortChange={getFilterSortDataList}
                title={AppConstant?.menus?.vendorPurchase}
                headerActions={<Button onClick={() => setOpen(true)}>{AppConstant?.add}</Button>}
                pagination={{
                    page: vendorPurchasePage?.currentPage || 1,
                    total: vendorPurchasePage?.totalCount || 0,
                    onChange: onPageChange,
                }}
            />
            <VendorPurchaseFormSheet
                open={open}
                onClose={formClose}
                vendorOptions={vendorComboOptions}
                formData={vendorPurchaseFormData}
                onChange={(patch: any) => setVendorPurchaseFormData({ ...vendorPurchaseFormData, ...patch })}
                onSubmit={createOrUpdateVendorPurchaseApi}
            />
            <ConfirmDialog
                open={!!deleteTarget}
                onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
                title="Delete vendor bill?"
                description={`${deleteTarget?.invoiceId ?? "This bill"} will be removed from the list and from the vendor's purchase totals.`}
                confirmLabel="Delete"
                cancelLabel={AppConstant?.cancel}
                destructive
                loading={deletePending}
                onConfirm={confirmDelete}
            />
            <VendorBillAttachmentViewer
                open={billAttachmentOpen}
                onClose={() => setBillAttachmentOpen(false)}
                entityId={billAttachmentEntityId}
            />
            <VendorBillAttachmentSheet
                open={billAttachmentSheetOpen}
                onClose={() => setBillAttachmentSheetOpen(false)}
                entityId={billAttachmentSheetEntityId}
            />
        </div>
    )
}

export default VendorPurchaseHistory
